import {
  camelCase,
  cloneDeep,
  filter,
  first,
  forEach,
  last,
  map,
  omit,
  sortBy,
} from 'lodash-es';
import { v4 as uuid } from 'uuid';
import {
  getCopyTitle,
} from '@/lib/utils/titleFormat';
import {
  docFromString,
  findDocNodes,
  toDocString,
} from '@discoveryedu/titan-text-editor';
import {
  createTopZPosition,
} from '@discoveryedu/titan-grid-layout';
import {
  EDITOR_CANVAS_WIDTH,
  EDITOR_CANVAS_HEIGHT,
  MODAL_CANVAS_WIDTH,
  MODAL_CANVAS_HEIGHT,
  MODAL_CANVAS_PLACE_MODULE_BLEED,
} from '@/lib/constants';
import placeModule from '@/lib/utils/placeModule';
import {
  bottomYPosition,
} from '@/lib/utils/grid';
import {
  newPageTitle,
} from '@/lib/utils/store';
import {
  removeWindowLinks,
  removeWordTracking,
} from '@/lib/utils/module';
import * as moduleDefaults from '@/lib/constants/moduleDefaults';
import i18next from '@/lib/i18next';
import { isGuid } from '@/lib/utils';
import chain from '@/lib/utils/chain';
// eslint-disable-next-line import/no-cycle
import {
  useAssetStore,
  useEditorStore,
  useToastStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export function copyModules({ modules, modalCopyMap }) {
  const moduleCopies = map(modules, (mod) => {
    const moduleCopy = {
      ...cloneDeep(mod),
      id: uuid(),
      // Remove 'id' from options if it's there.
      options: omit(cloneDeep(mod.options), ['id']),
    };

    if (modalCopyMap) {
      const targetPage = moduleCopy.options?.target_page;
      const action = moduleCopy.options?.action;

      // Use modalCopyMap to update links to modals
      if (action === 'window' && targetPage) {
        const newModalId = modalCopyMap[targetPage]?.id;
        // Only update if there is a mapping for target_page
        if (newModalId) {
          moduleCopy.options.target_page = newModalId;
        }
      }

      // Use modalCopyMap to update links to modals within a text module.
      if (mod.type === 'text') {
        const doc = docFromString(moduleCopy.options?.content);
        if (doc) {
          // Update links in the ProseMirror doc.
          const buttons = findDocNodes(doc, (node) => node['data-action'] === 'window');
          buttons.forEach((button) => {
            const updateButton = button;
            const buttonTargetPage = button['data-target-page'];
            const newModalId = modalCopyMap[buttonTargetPage]?.id;
            // Only update if there is a mapping for target_page
            if (newModalId) {
              updateButton['data-target-page'] = newModalId;
            }
          });
          moduleCopy.options.content = toDocString(doc);
        }
      }
    }

    return moduleCopy;
  });

  return moduleCopies;
}

export function copyPages({
  pages,
  modalCopyMap,
  atSourcePosition = false,
}) {
  const editorStore = useEditorStore();
  const {
    currentPageIndex,
    pagesSorted,
    currentPage,
  } = editorStore;
  const currentPageSortIndex = currentPage.sort_index;
  const currentPageIsLastPage = last(pagesSorted)?.id === currentPage?.id;
  const lastPageSortIndex = last(pagesSorted)?.sort_index || 1;
  const maxPageIndex = pagesSorted.length - 1;
  let fromCurrentPageIncrement = 0;
  return map(pages, (page) => {
    const titleExists = pagesSorted.find((p) => p.title === page.title);

    /*
      Calculate a new sort_index for each new page
    */
    const pageIndex = pagesSorted.findIndex((p) => p.id === page.id);
    let pageSortIndex;
    if (atSourcePosition) {
      // Duplicate: each copy lands right after its own source page,
      // interleaving copies with originals ([A, A', B, B', ...]).
      if (pageIndex === maxPageIndex) {
        pageSortIndex = lastPageSortIndex + 1;
      } else {
        const nextSortIndex = pagesSorted[pageIndex + 1].sort_index;
        pageSortIndex = (pagesSorted[pageIndex].sort_index + nextSortIndex) / 2;
      }
    } else if (currentPageIsLastPage) {
      fromCurrentPageIncrement += 1;
      pageSortIndex = currentPageSortIndex + fromCurrentPageIncrement;
    } else {
      // Fit all pasted pages between the current page and its next neighbor.
      const nextSortIndex = pagesSorted[currentPageIndex + 1].sort_index;
      const sortIndexDiff = nextSortIndex - currentPageSortIndex;
      fromCurrentPageIncrement += sortIndexDiff / (pages.length + 1);
      pageSortIndex = currentPageSortIndex + fromCurrentPageIncrement;
    }
    const pageCopy = {
      ...cloneDeep(page),
      id: uuid(),
      title: titleExists ? newPageTitle(page.title) : page.title,
      modules: copyModules({
        modules: page.modules,
        modalCopyMap,
      }),
      notes: copyModules({
        modules: page.notes,
        modalCopyMap,
      }),
      sort_index: pageSortIndex,
      options: {
        ...cloneDeep(page.options),
        group_id: atSourcePosition
          ? page.options?.group_id
          : currentPage.options?.group_id,
      },
    };
    return pageCopy;
  });
}

export function copyModals({
  modals,
  forceCopy = false,
}) {
  const editorStore = useEditorStore();
  const modalCopyMap = {};
  const modalTitles = editorStore.modalCanvases.map((c) => c.title);
  forEach(modals, (modal) => {
    /*
      Determine if the modal we're copying has already been pasted or already exists.
      If the source_id matches the id of the modal we're pasting, that means
      this modal has already been pasted so we will not paste it again.
      The source_id on a pasted modal gets removed as soon as the user makes a change
      to the modal.
    */
    const existingModal = editorStore.modalCanvases.find(
      (c) => c.id === modal.id || c.options?.source_id === modal.id,
    );
    const existingModalSourceId = existingModal?.options?.source_id;
    // Only copy the modal if it doesn't already exist or we are forcing a copy.
    const shouldCopy = forceCopy || !existingModal;
    if (shouldCopy) {
      const title = getCopyTitle(modal.title, modalTitles);
      const modalCopy = {
        ...cloneDeep(modal),
        id: uuid(),
        title,
        modules: copyModules({ modules: modal.modules }),
        options: {
          ...cloneDeep(modal.options),
          /*
            When we forceCopy, we copy the modal even though it already
            exists. In this case, do not set source_id. This is used to duplicate
            a modal in the UI.
          */
          source_id: forceCopy ? undefined : modal.id,
        },
      };
      modalTitles.push(title);
      modalCopyMap[modal.id] = modalCopy;
    } else if (existingModalSourceId === modal.id) {
      /*
        If we found an existing modal that is a copy of the one we're pasting,
        then add a mapping to the modalCopyMap that can be used later to update
        modal button links in modules being pasted.
      */
      modalCopyMap[modal.id] = existingModal;
    }
  });
  return {
    modalCopyMap,
    modals: Object.values(modalCopyMap),
  };
}

export function getModalsForModule(module, modals) {
  const editorStore = useEditorStore();
  const modalCopies = [];

  const addModalById = (modalId) => {
    const findModalById = (m) => m.id === modalId;

    // If we've already copied this modal, then exit.
    if (modalCopies.find(findModalById)) return;

    // Search the specified modals array or the app store.
    const searchModals = modals || editorStore.draft.modals;

    // Get the modal if it exists.
    const modal = searchModals.find(findModalById);

    // If the modal exists, then make a copy.
    if (modal) {
      modalCopies.push(cloneDeep(modal));
    }
  };

  const targetPage = module.options?.target_page;
  const action = module.options?.action;

  // Add modals attached to this module.
  if (action === 'window' && targetPage) {
    addModalById(targetPage);
  }

  // Add modals used by links within the text module content.
  if (module.type === 'text') {
    const doc = docFromString(module.options?.content);
    if (doc) {
      const buttons = findDocNodes(doc, (node) => node['data-action'] === 'window');
      buttons.forEach((button) => {
        addModalById(button['data-target-page']);
      });
    }
  }

  return modalCopies;
}

export function getModalsForPage(page) {
  const pageModules = [...page.modules, ...(page.notes || [])];
  return chain(pageModules)
    .map((m) => getModalsForModule(m))
    .flatten()
    .uniqBy('id')
    .value();
}

export function getModalsForPages(pages) {
  return chain(pages)
    .map((m) => getModalsForPage(m))
    .flatten()
    .uniqBy('id')
    .value();
}

export function getPastePosition({
  module,
  canvasName,
  sourceCanvasName,
  getModulePosition,
  modulesOrderedByZ,
}) {
  const editorStore = useEditorStore();

  /*
    Try to preserve the pasted module's position if we know
    the source canvas and the source canvas is the same as the
    target canvas and we're not going to or from the notes canvas.
  */
  const tryPreserveModPosition = !!sourceCanvasName
    && sourceCanvasName !== 'notes'
    && canvasName !== 'notes'
    && sourceCanvasName === canvasName;

  const modalCanvas = editorStore.modalCanvases?.find(
    (modal) => modal.id === editorStore.currentModalCanvasId,
  );

  const canvasInfo = canvasName === 'modalModules'
    ? {
      width: modalCanvas?.options?.width || MODAL_CANVAS_WIDTH,
      height: modalCanvas?.options?.height || MODAL_CANVAS_HEIGHT,
      bleed: MODAL_CANVAS_PLACE_MODULE_BLEED,
    }
    : {
      width: EDITOR_CANVAS_WIDTH,
      height: editorStore.draftType === 'board'
        ? editorStore.currentPage?.options?.height || EDITOR_CANVAS_HEIGHT
        : EDITOR_CANVAS_HEIGHT,
    };

  /*
    When copy and pasting from one canvas to another, try
    to preserve the module's position. If the module's position
    is inside the bounds of the target canvas, then paste the
    module to its original position.
  */
  if (tryPreserveModPosition) {
    const position = {
      x: module.layout.x_position,
      y: module.layout.y_position,
    };
    let canvasWidth;
    let canvasHeight;
    if (canvasName === 'modalModules') {
      canvasWidth = modalCanvas?.options?.width || MODAL_CANVAS_WIDTH;
      canvasHeight = modalCanvas?.options?.height || MODAL_CANVAS_HEIGHT;
    } else {
      canvasWidth = EDITOR_CANVAS_WIDTH;
      canvasHeight = editorStore.draftType === 'board'
        ? editorStore.currentPage?.options?.height || EDITOR_CANVAS_HEIGHT
        : EDITOR_CANVAS_HEIGHT;
    }
    const right = position.x + module.layout.width;
    const bottom = position.y + module.layout.height;
    const leftInside = position.x >= 0 && position.x <= canvasWidth;
    const rightInside = right >= 0 && right <= canvasWidth;
    const topInside = position.y >= 0 && position.y <= canvasHeight;
    const bottomInside = bottom >= 0 && bottom <= canvasHeight;
    const moduleInsideCanvas = (topInside && leftInside)
      || (topInside && rightInside)
      || (bottomInside && rightInside)
      || (bottomInside && leftInside);

    if (moduleInsideCanvas) {
      /*
        If the module's current position is inside the canvas it's being
        pasted into, then use the that position as starting placement position.
      */
      canvasInfo.defaultPosition = position;
    }
  }

  /*
    If we were not able to preserve the pasted module's position,
    then use the provided function to resolve the module's new
    position on the canvas.
  */
  if (!canvasInfo.defaultPosition && getModulePosition) {
    canvasInfo.defaultPosition = getModulePosition(module);
  }

  // Get the final postion to paste the module to.
  const coordinates = placeModule(
    modulesOrderedByZ,
    module,
    canvasInfo,
  );

  return {
    x_position: coordinates.x,
    y_position: coordinates.y,
    z_position: createTopZPosition({
      itemsOrderedByZ: modulesOrderedByZ,
      zProperty: 'layout.z_position',
    }),
  };
}

export function isPasteChangingLayout({ canvasName, sourceCanvasName }) {
  /*
    Returns true if the module is being pasted to a canvas that has a
    different coordinate system than the source.
  */
  return canvasName
    && sourceCanvasName
    && canvasName !== sourceCanvasName
    && [canvasName, sourceCanvasName].includes('notes');
}

export function setPastedModulesDefaultLayout({ modules, canvasName }) {
  modules.forEach((pastedModule) => {
    const updateModule = pastedModule;
    const layout = cloneDeep(pastedModule.layout);
    const assetStore = useAssetStore();
    const typeKey = `${camelCase(pastedModule?.type)}ModuleDefault`;
    const targetHasGridDisabled = canvasName !== 'notes';
    const assetId = pastedModule?.options?.asset_id;
    const asset = assetStore.assets[assetId];

    // Sets the default layout for the pasted module.
    if (moduleDefaults[typeKey]) {
      const defaultModule = moduleDefaults[typeKey](targetHasGridDisabled, asset);
      updateModule.layout = {
        ...layout,
        ...defaultModule.layout,
      };
    }
  });

  return modules;
}

export function canAddModuleToNotes(mod) {
  // These modules types are not allowed in notes.
  const excludedTypes = [
    'prompt',
    'single_selection',
    'multiple_selection',
    'block',
    'shape',
  ];
  return !excludedTypes.includes(mod.type);
}

export function canAddSomeModulesToNotes(modules = []) {
  // Allow if any modules can be added to the notes.
  return modules.some(canAddModuleToNotes);
}

export function getPastableModules({ pasteData, canvasName }) {
  const modules = pasteData.modules || [];

  if (canvasName === 'notes') {
    // Only certain modules are allowed in the notes.
    return modules.filter(canAddModuleToNotes);
  }

  return modules;
}

export function setPastedModulesOptions({ modules, canvasName }) {
  modules.forEach((pastedModule) => {
    const updateModule = pastedModule;
    /*
      Gets adjusted options for the module based on the canvas
      it is being pasted into.
    */
    const mod = cloneDeep(pastedModule);
    if (canvasName === 'notes') {
      // Video options not allowed in notes modules.
      if (mod.options?.auto_advance) {
        mod.options.auto_advance = false;
      }
      if (mod.options?.auto_play) {
        mod.options.auto_play = false;
      }
      if (mod.options?.loop_video) {
        mod.options.loop_video = false;
      }
      // Word tracking not allowed in the notes tab.
      if (mod.options?.word_tracking) {
        removeWordTracking(mod);
      }
    } else if (canvasName === 'modalModules') {
      // Auto advance video option not allowed on the modal canvas.
      if (mod.options?.auto_advance) {
        mod.options.auto_advance = false;
      }
      // Remove window links from advanced buttons and assets.
      removeWindowLinks({ module: mod });
    }

    updateModule.options = mod.options;
  });

  return modules;
}

export function setPastedModulesLayout({
  modules,
  canvasName,
  sourceCanvasName,
  sourceModulesPageId,
  sourceDraftId,
  getModulePosition,
}) {
  const updatedModules = [];

  modules.forEach((pastedModule) => {
    const updateModule = pastedModule;
    let layout = cloneDeep(pastedModule.layout);
    /*
      Get the layout object for the module being pasted based
      on the canvas it's being pasted into.
    */
    if (canvasName === 'notes') {
      const editorStore = useEditorStore();
      // if we're jumping across canvases, we need to put the modules on the bottom
      const addToBottom = (canvasName === 'notes' && sourceCanvasName !== 'notes')
        || editorStore.currentPage.id !== sourceModulesPageId
        || editorStore.draft.id !== sourceDraftId
        || modules?.length > 1;
      // Rotation not allowed in notes
      layout.rotate_degree = 0;
      if (addToBottom) {
        // Ensure that already positioned pasted modules are included.
        const positionedModules = [
          ...updatedModules,
          ...editorStore.currentPage.notes,
        ];

        // Place module at the bottom of the notes
        layout.y_position = bottomYPosition(positionedModules);
      } else {
        // Otherwise add directly below the copied module.
        layout.y_position += pastedModule.layout.height;
      }
    } else {
      // Get the modules currently on the canvas sorted by z_position.
      const editorStore = useEditorStore();
      let modulesOrderedByZ = canvasName === 'modalModules'
        ? editorStore.getModulesByModalIdOrderedByZPosition(editorStore.currentModalCanvasId)
        : editorStore.modulesOrderedByZPosition;

      /*
        Ensure that any already updated modules are included
        and the modules are resorted by their z_position. We want
        any pasted modules that we've already assigned a position for
        to be considered when finding a position for the current
        pasted module.
      */
      modulesOrderedByZ = sortBy([
        ...modulesOrderedByZ,
        ...updatedModules,
      ], (m) => m.layout?.z_position);

      // Create a layout with a new position for the pasted module.
      layout = {
        ...layout,
        ...getPastePosition({
          module: pastedModule,
          canvasName,
          sourceCanvasName,
          getModulePosition,
          modulesOrderedByZ,
        }),
      };
    }

    updateModule.layout = layout;
    updatedModules.push(updateModule);
  });

  return modules;
}

export function updateModulesOnPaste({
  canvasName,
  pasteData,
  pastedModules,
}) {
  const updateModules = [];
  const editorStore = useEditorStore();

  /*
    If we copied from the notes and are pasting to the notes, then
    move existing modules down to make room for the pasted modules.
  */
  if (canvasName === 'notes' && pasteData.canvasName === 'notes') {
    // Sort the pasted modules by their y_position.
    const sortedCopies = sortBy(pastedModules, (m) => m.layout.y_position);

    // Get the starting y_position for the pasted modules.
    const pasteStartingY = first(sortedCopies).layout.y_position;

    // Get the bottommost pasted module.
    const lastCopy = last(sortedCopies);

    // Get the next position after the bottom of the bottommost pasted module.
    const pasteNextY = lastCopy.layout.y_position + lastCopy.layout.height;

    // Get the total height of the pasted modules.
    const pasteHeight = pasteNextY - pasteStartingY;

    // Find any existing modules that need to be moved downward.
    const modulesToMove = filter(
      editorStore.currentPage.notes,
      (m) => m.layout.y_position >= pasteStartingY,
    );

    /*
      Move each existing module that is at or below the paste starting
      y_position downward by the total paste height.
      This will make just enough room for the pasted modules.
    */
    modulesToMove.forEach((modToMove) => {
      const modToMoveCopy = cloneDeep(modToMove);
      modToMoveCopy.layout.y_position += pasteHeight;
      updateModules.push(modToMoveCopy);
    });
  }

  return updateModules;
}

export async function getPasteModuleFromAssetId({ pasteData, canvasName }) {
  const assetStore = useAssetStore();
  const toastStore = useToastStore();
  // Get the asset for the asset id being pasted.
  const result = await assetStore[types.GET_ASSET]({
    assetId: pasteData.assetId,
    fromCache: true,
  });

  if (result.asset) {
    const { asset } = result;
    const gridIsDisabled = canvasName !== 'notes';
    // Create a new asset module for the pasted asset id.
    const module = moduleDefaults.assetModuleDefault(gridIsDisabled, asset);
    module.options.asset_id = asset.id;
    return module;
  }

  const errorMsg = i18next.t('Paste failed: invalid asset');
  toastStore[types.SET_STUDIO_TOAST]({ type: 'error', message: errorMsg });
  return null;
}

export function validatePasteData({ pasteData, moduleToReplace, canvasName }) {
  const toastStore = useToastStore();
  const editorStore = useEditorStore();
  if (!pasteData || !canvasName) {
    return false;
  }
  if (moduleToReplace && !pasteData.modules?.length) {
    return false;
  }
  if (pasteData.assetId && !isGuid(pasteData.assetId)) {
    const errorMsg = i18next.t('Asset id must be a GUID');
    toastStore[types.SET_STUDIO_TOAST]({ type: 'error', message: errorMsg });
    return false;
  }
  if (
    pasteData.modules?.length
    && canvasName === 'notes'
    && !canAddSomeModulesToNotes(pasteData.modules)
  ) {
    const errorMsg = i18next.t('The pasted modules are not allowed in the Teacher Notes');
    toastStore[types.SET_STUDIO_TOAST]({ type: 'error', message: errorMsg });
    return false;
  }
  if (pasteData.pages && pasteData.draftType === 'board' && editorStore.draftType !== 'board') {
    toastStore[types.SET_STUDIO_TOAST]({
      type: 'error',
      message: i18next.t('A board page cannot be pasted into a slideshow.'),
      position: 'top',
    });
    return false;
  }
  if (pasteData.pages && pasteData.draftType !== 'board' && editorStore.draftType === 'board') {
    toastStore[types.SET_STUDIO_TOAST]({
      type: 'error',
      message: i18next.t('A slide cannot be pasted into a board.'),
      position: 'top',
    });
    return false;
  }
  const hasFullSlideAssets = !!pasteData.pages?.some((page) => page.options?.asset_id);
  if (editorStore.draftType !== 'lesson' && hasFullSlideAssets) {
    toastStore[types.SET_STUDIO_TOAST]({
      type: 'error',
      message: i18next.t('Full-slide assets can only be used in lessons.'),
      position: 'top',
    });
    return false;
  }
  return true;
}

export function validatePasteAssetId(assetId) {
  const toastStore = useToastStore();
  const chunksSplitHyphen = assetId.split('-');
  const hasFiveChunksHyphen = chunksSplitHyphen.length === 5;
  const specialChars = /[!@#$%^&*()_+=[\]{};':"\\|,.<>/?]/;
  const hasFiveChunksSpecialChars = assetId.split(specialChars).length === 5;
  /*
    Check a few special cases:
    1. Pattern is 5 chunks, split with a '-', but the last chunk has more
    or less than 12 characters.
    2. Pattern is separated with special characters, but is the correct length
    of 36 characters.
  */
  if (
    (hasFiveChunksHyphen && last(chunksSplitHyphen).length !== 12)
    || (hasFiveChunksSpecialChars && assetId.length === 36)
  ) {
    toastStore[types.SET_STUDIO_TOAST]({
      type: 'error',
      message: i18next.t('Paste failed: Invalid asset guid'),
      position: 'top',
    });
    return false;
  }
  return true;
}

export default {
  canAddModuleToNotes,
  canAddSomeModulesToNotes,
  copyModals,
  copyModules,
  copyPages,
  getModalsForModule,
  getModalsForPage,
  getModalsForPages,
  getPastableModules,
  getPasteModuleFromAssetId,
  getPastePosition,
  isPasteChangingLayout,
  updateModulesOnPaste,
  setPastedModulesDefaultLayout,
  setPastedModulesLayout,
  setPastedModulesOptions,
  validatePasteAssetId,
  validatePasteData,
};
